import { useState } from 'react';
import { Zap, X, Loader as Loader2 } from 'lucide-react';
import { useApi } from '../../lib/useApi';
import type { ContactWithAccount, ContactUseCaseScore } from '../../types';

interface Props {
  selectedContacts: ContactWithAccount[];
  onClear: () => void;
  onComplete: () => void;
}

const MAX_CONCURRENT = 3;

export default function BatchGenerationBar({ selectedContacts, onClear, onComplete }: Props) {
  const { apiGet, apiPost } = useApi();
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, skipped: 0 });

  if (selectedContacts.length === 0) return null;

  const generate = async () => {
    setGenerating(true);
    const total = selectedContacts.length;
    let done = 0;
    let skipped = 0;
    setProgress({ done: 0, total, skipped: 0 });

    // Resolve each contact's highest-relevance use case via the migrated
    // account-scoped contact-scores read (one fetch per distinct account), then
    // pick the top score per contact client-side (legacy ordered by
    // use_case_relevance_score desc, limit 1 per contact).
    const accountIds = [...new Set(selectedContacts.map(c => c.account_id))];
    const scoresByAccount = new Map<string, ContactUseCaseScore[]>();
    await Promise.all(
      accountIds.map(async (accountId) => {
        try {
          const data = await apiGet<{ scores: ContactUseCaseScore[] }>(
            `/v1/account/${accountId}/contact-scores`,
          );
          scoresByAccount.set(accountId, data.scores || []);
        } catch {
          scoresByAccount.set(accountId, []);
        }
      })
    );

    const contactsWithUseCases = selectedContacts.map((c) => {
      const top = (scoresByAccount.get(c.account_id) || [])
        .filter(s => s.contact_id === c.id)
        .sort((a, b) => (b.use_case_relevance_score ?? 0) - (a.use_case_relevance_score ?? 0))[0];
      return { contact: c, useCaseTitle: top?.use_case_title || null };
    });

    const queue = contactsWithUseCases.filter(item => {
      if (!item.useCaseTitle) {
        skipped++;
        done++;
        setProgress({ done, total, skipped });
        return false;
      }
      return true;
    });

    for (let i = 0; i < queue.length; i += MAX_CONCURRENT) {
      const batch = queue.slice(i, i + MAX_CONCURRENT);
      await Promise.all(
        batch.map(async ({ contact, useCaseTitle }) => {
          try {
            await apiPost('/v1/generate-contact-outreach', {
              account_id: contact.account_id,
              contact_id: contact.id,
              use_case_title: useCaseTitle,
            });
          } catch {
            // continue with next
          }
          done++;
          setProgress({ done, total, skipped });
        })
      );
    }

    setGenerating(false);
    onComplete();
  };

  return (
    <div className="fixed inset-x-3 bottom-3 z-40 sm:inset-x-auto sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2">
      <div className="flex w-full flex-col gap-3 rounded-lg bg-app-chrome px-4 py-4 text-white shadow-2xl sm:min-w-[400px] sm:flex-row sm:items-center sm:gap-4 sm:px-6">
        {!generating ? (
          <>
            <span className="text-sm font-semibold">{selectedContacts.length} contacts selected</span>
            <button
              onClick={generate}
              className="flex min-h-10 items-center justify-center gap-2 bg-app-accent px-4 py-2 text-sm font-semibold text-app-accent-text transition-colors hover:brightness-95"
            >
              <Zap className="w-4 h-4" />
              Generate Messages
            </button>
            <button onClick={onClear} className="absolute right-2 top-2 p-1.5 text-app-chrome-muted transition-colors hover:text-white sm:static" aria-label="Clear selection">
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <Loader2 className="w-5 h-5 animate-spin text-forest-400" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold">
                  Generating... {progress.done}/{progress.total}
                </span>
                {progress.skipped > 0 && (
                  <span className="text-xs text-app-warning">{progress.skipped} skipped (no use case)</span>
                )}
              </div>
              <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden" role="progressbar" aria-label="Contact message generation" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done}>
                <div
                  className="h-full bg-app-accent rounded-full transition duration-300"
                  style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
